/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IoCloseSharp } from 'react-icons/io5';
import { cloneDeep } from 'lodash';
import Store from '../../store/store';
import { openModal } from '../../actions/modalActions';
import { toggleMenu } from '../../actions/dropDownActions';
import {
  createTeam,
  uploadTeamImage,
  addTeam,
  clearTeamData,
  updateTeam,
  deleteTeam,
  restoreTeam,
  addRole,
  removeRole
} from '../../actions/teamActions';
import { fetchUsers } from '../../actions/userActions';
import modalTypes from '../../constants/modalTypes';
import Metrics from './metrics';
import ShortNameField from '../Common/shortNameField';
import ModalNavigation from '../Common/modalNavigation';
import Holon from '../Buttons/holon';
import Dropdown from '../Buttons/dropdown';
import TeamDetails from './teamDetails';
import { allowsThroughOrganizationOrTeam } from '../../utils/authorization';
import {
  getSelectedTeamAndMembers,
  getTeamSprints
} from '../../selectors/teamSelector';

class TeamDetailCard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isShortNameValid: true,
      inputEdited: false,
      droppedFileUrl: '',
      droppedFile: null,
      activeTab: 'details'
    };
  }

  handleFieldChange = (field) => {
    let { team } = this.props;
    team = { ...team, ...field };
    this.props.addTeam(team);
  };

  changeRole = (userId, roleType) => {
    const { team } = this.props;
    const updatedTeam = cloneDeep(team);
    const user = updatedTeam.members.find((member) => member.id === userId);
    const role = user.team_roles.find(
      (r) => r.type === roleType && r.team_id === team.id
    );
    if (role) {
      const roles = user.team_roles.slice(0);
      user.team_roles = roles.filter((r) => {
        if (r.team_id === updatedTeam.id && r.type === roleType) return false;
        return true;
      });
      if (team.id > 0) this.props.removeRole(team.id, role.id, user.id);
    } else {
      const newRole = { type: roleType, team_id: updatedTeam.id };
      user.team_roles.push(newRole);
      if (team.id > 0) this.props.addRole(team.id, user.id, roleType);
    }
    const members = updatedTeam.members.slice(0);
    updatedTeam.members = members.map((u) => {
      if (u.id === user.id) {
        return user;
      }
      return u;
    });
    this.props.addTeam(updatedTeam);
  };

  updateTeam = (e) => {
    const { team } = this.props;
    let field = e;
    if (e.target && e.target.name) {
      field = { [e.target.name]: e.target.value };
    }
    const fieldName = Object.keys(field)[0];
    if (team[fieldName] === field[fieldName]) return null;
    if (fieldName === 'short_name' && !field.short_name) {
      return this.setState({ isShortNameValid: false });
    }

    this.props.updateTeam(team.id, field);
    this.setState({ inputEdited: true });
    return null;
  };

  handleSave = (event) => {
    event.preventDefault();
    const { team } = this.props;
    const organizationId = team.members.length > 0 ? null : this.props.parentId;
    if (!team.short_name) {
      this.setState({ isShortNameValid: false });
      return;
    }
    if (team.short_name) {
      if (team.id > 0) {
        this.props.updateTeam(team.id, team);
      } else {
        team.level = this.props.level;
        this.props.createTeam(team, organizationId);
      }
      this.closeModal();
    } else {
      this.setState({ inputEdited: true });
    }
  };

  // toggleDropDownMenu = (e, userId, props) => {
  //   e.stopPropagation();
  //   const { target: element } = e;
  //   const { currentUser } = this.props;
  //   const classes = 'planwall-drop-menu';
  //   const dropDown = {
  //     element,
  //     type: 'updateMember',
  //     classes,
  //     props: { ...props, currentUser },
  //     id: userId
  //   };
  //   this.props.toggleMenu(dropDown);
  // };

  uploadFile = (file) => {
    const { team } = this.props;
    const reader = new FileReader();
    reader.onloadend = () => {
      this.setState(
        { droppedFileUrl: reader.result, droppedFile: file },
        () => {
          if (team.id > 0) {
            this.props.uploadTeamImage(
              this.state.droppedFile,
              this.state.droppedFileUrl,
              this.props.team.id
            );
          } else {
            const image = {
              droppedFile: this.state.droppedFile,
              droppedFileUrl: this.state.droppedFileUrl,
              type: this.props.type
            };
            const newTeam = { ...team, image };
            this.props.addTeam(newTeam);
          }
        }
      );
    };

    if (file) {
      reader.readAsDataURL(file);
    } else {
      this.setState({ droppedFileUrl: '', droppedFile: null });
    }
  };

  handleFileUpload = (event) => {
    const droppedFiles = event.target.files;
    if (droppedFiles.length > 0) {
      this.uploadFile(droppedFiles[0]);
    }
  };

  handleFileDrop = (item, monitor) => {
    if (monitor) {
      const droppedFiles = monitor.getItem().files;
      this.uploadFile(droppedFiles[0]);
    }
  };

  addMember = () => {
    const { team } = this.props;
    Store.dispatch(openModal(modalTypes.newTeamMember, { team }));
  };

  deleteTeam = () => {
    const { team } = this.props;
    if (
      window.confirm(
        `Are you sure you want to delete Team ${team.short_name}?  You can restore it later using the recycle bin.`
      )
    ) {
      this.props.deleteTeam(team);
      this.props.onClose(null, 'teamCard');
    }
  };

  restoreTeam = () => {
    this.props.restoreTeam(this.props.team);
    this.props.onClose(null, 'teamCard');
  };

  closeModal = () => {
    this.props.clearTeamData();
    this.props.onClose(null, 'teamCard');
  };

  changeTab = (tabName) => {
    this.setState({ activeTab: tabName });
  };

  handlePermissions = () => {
    const teamId = this.props.id;

    const { team } = this.props;

    const organizationId = team.organization_id || this.props.parentId;

    if (teamId) {
      const allowCreate = allowsThroughOrganizationOrTeam(
        'create_teams',
        organizationId,
        teamId
      );
      const allowEdit = allowsThroughOrganizationOrTeam(
        'update_teams',
        organizationId,
        teamId
      );
      const allowDelete = allowsThroughOrganizationOrTeam(
        'destroy_teams',
        organizationId,
        teamId
      );
      const allowRestore = allowsThroughOrganizationOrTeam(
        'restore_teams',
        organizationId,
        teamId
      );
      const allowAddMembers = allowsThroughOrganizationOrTeam(
        'add_members_teams',
        organizationId,
        teamId
      );
      const allowUpdateRoles =
        allowsThroughOrganizationOrTeam(
          'add_role_teams',
          organizationId,
          teamId
        ) &&
        allowsThroughOrganizationOrTeam(
          'remove_role_teams',
          organizationId,
          teamId
        );

      const allowUpdateUser = allowsThroughOrganizationOrTeam(
        'update_user_users',
        organizationId,
        teamId
      );

      const userPermissions = {
        allowEdit,
        allowDelete,
        allowCreate,
        allowRestore,
        allowAddMembers,
        allowUpdateRoles,
        allowUpdateUser
      };

      return userPermissions;
    }
    return {
      allowEdit: true,
      allowCreate: true
    };
  };

  renderActiveTab = (
    allowEdit,
    allowDelete,
    allowCreate,
    allowRestore,
    allowAddMembers,
    allowUpdateRoles,
    allowUpdateUser
  ) => {
    const { activeTab } = this.state;
    const { team, users, sprints, isUploading } = this.props;

    switch (activeTab) {
      case 'details':
        return (
          <TeamDetails
            team={team}
            users={users}
            isUploading={isUploading}
            onChange={this.handleFieldChange}
            onUpdate={this.updateTeam}
            handleFileDrop={this.handleFileDrop}
            handleFileUpload={this.handleFileUpload}
            addMember={this.addMember}
            changeRole={this.changeRole}
            allowEdit={allowEdit}
            allowDelete={allowDelete}
            allowCreate={allowCreate}
            allowAddMembers={allowAddMembers}
            allowUpdateRoles={allowUpdateRoles}
            allowUpdateUser={allowUpdateUser}
          />
        );
      case 'metrics':
        return (
          team &&
          team.id && (
            <Metrics activeTab={activeTab} team={team} sprints={sprints} />
          )
        );
      default:
        return null;
    }
  };

  render = () => {
    if (!this.props.isOpen) {
      return null;
    }
    const { team, isLoading, sprints, users } = this.props;

    const {
      allowEdit,
      allowDelete,
      allowCreate,
      allowRestore,
      allowAddMembers,
      allowUpdateRoles,
      allowUpdateUser
    } = this.handlePermissions();
    const { activeTab, isShortNameValid } = this.state;
    const tabs = team && team.id ? ['details', 'metrics'] : ['details'];

    return (
      <div className="detail-modal team-modal" tabIndex="-1" role="dialog">
        <div className="detail-dialog" role="document">
          {isLoading ? <div id="loader" /> : null}
          <div
            className={`modal-content profile-content ${
              isLoading ? 'blur-loading' : ''
            }`}
          >
            <div className="modal-header profile-header detail-header team-modal-header">
              <div>
                <Dropdown
                  handleDelete={this.deleteTeam}
                  handleRestore={this.restoreTeam}
                  allowDelete={allowDelete}
                  allowEdit={allowEdit}
                  allowRestore={allowRestore}
                  deleted={team.deleted_at}
                />
              </div>
              <div className="text-center holon">
                <Holon disabled />
              </div>
              <div className="detail-close">
                <button
                  type="button"
                  className="btn-close"
                  onClick={this.closeModal}
                >
                  <IoCloseSharp size="2em" />
                </button>
              </div>
            </div>
            <div className="modal-body team-modal-body">
              <div className="modal-body-input">
                <ShortNameField
                  autoFocus
                  shortName={isShortNameValid ? team.short_name : ''}
                  onNameChange={
                    team.id > 0 ? this.updateTeam : this.handleFieldChange
                  }
                  inputEdited={this.state.inputEdited}
                  label="Name"
                  allowEdit={allowEdit}
                  isValid={isShortNameValid}
                />
              </div>
              <ModalNavigation
                tabs={tabs}
                activeTab={activeTab}
                changeTab={this.changeTab}
              />

              {this.renderActiveTab(
                allowEdit,
                allowDelete,
                allowCreate,
                allowRestore,
                allowAddMembers,
                allowUpdateRoles,
                allowUpdateUser
              )}
            </div>
            {!team.id && (
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-xs button-lavender"
                  onClick={this.handleSave}
                >
                  <strong>save</strong>
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-default button-normal"
                  onClick={this.closeModal}
                >
                  <strong>Cancel</strong>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
}

TeamDetailCard.propTypes = {
  level: PropTypes.number,
  team: PropTypes.object,
  users: PropTypes.array,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  createTeam: PropTypes.func
};

TeamDetailCard.defaultProps = {
  team: {
    short_name: '',
    description: '',
    members: []
  },
  level: 1
};

const mapStateToProps = (state, ownProps) => {
  const allTeams = state.teamReducer.teams;
  const team = getSelectedTeamAndMembers(state, ownProps);

  const teamSprints = getTeamSprints(state, ownProps);

  return {
    team,
    allTeams,
    sprints: teamSprints,
    users: state.userReducer.users,
    isLoading: state.teamReducer.isFetching,
    currentUser: state.userReducer.user
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createTeam,
      fetchUsers,
      uploadTeamImage,
      addTeam,
      clearTeamData,
      updateTeam,
      deleteTeam,
      restoreTeam,
      addRole,
      removeRole,
      toggleMenu
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(TeamDetailCard);
